import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { Button, Input, Select, Asset } from "@thenounproject/lingo-core";

import AssetDownloadOptionThumbnail from "./AssetDownloadOptionThumbnail";
import ExportOption from "./ExportOption";
import { CopyAsset, DownloadAsset, IconStatus } from "./AssetDownloadPopup";

const CustomSizeInput = styled(Input).attrs({
  styleOverrides: {
    mb: "none",
    ml: "xs",
    flexShrink: 1,
    width: "100%",
  },
})``;

type Props = {
  asset: Asset;
  downloadAsset: DownloadAsset;
  copyAsset: CopyAsset;
  copyStatus?: { fileType: string; status: IconStatus };
  downloadStatus?: { fileType: string; status: Exclude<IconStatus, null> };
};

const CustomExportOption: React.FC<Props> = ({
  asset,
  downloadAsset,
  copyAsset,
  copyStatus,
  downloadStatus,
}) => {
  function getCustomTypeOptions() {
    if (!asset.meta.filecuts) return null;
    const { availableTypes } = asset.meta.filecuts;
    return availableTypes.filter(({ enabled }) => enabled);
  }

  const availableTypes = getCustomTypeOptions();

  const [customSizeInput, setCustomSizeInput] = useState("1x");
  const [customTypeSelect, setCustomTypeSelect] = useState(
    (availableTypes && availableTypes.length && availableTypes[0].type) || ""
  );

  function downloadCustomAsset(e: React.MouseEvent) {
    if (e) e.preventDefault();
    if (!customSizeInput) return;
    downloadAsset("custom", customTypeSelect, customSizeInput, undefined, e);
  }

  function copyCustomAsset(e: React.MouseEvent) {
    if (e) e.preventDefault();
    if (!customSizeInput) return;
    copyAsset("custom", customTypeSelect, customSizeInput, asset, undefined, e);
  }

  const getCopyIcon = useCallback(() => {
    if (copyStatus?.status === "success") return "info.checkmark";
    if (copyStatus?.status === "error") return "info.error";
    return "action.copy";
  }, [copyStatus]);

  const getDownloadIcon = useCallback(() => {
    if (downloadStatus?.status === "error") {
      return "info.error";
    }
    if (downloadStatus?.status === "success") {
      return "info.checkmark";
    }
    return "download";
  }, [downloadStatus]);

  if (!availableTypes?.length) return null;

  type ExportType = Asset["meta"]["filecuts"]["availableTypes"][0];

  const customTypesByName: Record<string, ExportType> = availableTypes.reduce(
    (reduced, typeObj) => {
      reduced[typeObj.type] = typeObj;
      return reduced;
    },
    {}
  );

  const opts = availableTypes.map(t => {
    return { value: t.type, label: t.type };
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const canResize = type => customTypesByName[type] && customTypesByName[type].resizable;

  return (
    <ExportOption data-test="dl-custom">
      <AssetDownloadOptionThumbnail asset={asset} />
      <Select
        buttonStyle="dropdown"
        options={opts}
        size="small"
        value={customTypeSelect}
        onChange={t => {
          setCustomTypeSelect(t);
          setCustomSizeInput(canResize(t) ? customSizeInput : "1x");
        }}
      />
      <CustomSizeInput
        disabled={!canResize(customTypeSelect)}
        size="small"
        placeholder="2x, 300w"
        value={customSizeInput}
        onChange={e => setCustomSizeInput(e.target.value)}
      />
      {["PNG", "SVG"].includes(customTypeSelect) && (
        <Button
          flexShrink="0"
          ml="xs"
          id="copy-button-custom"
          onClick={copyCustomAsset}
          size="small"
          icon={getCopyIcon()}
          loading={customTypeSelect === copyStatus?.fileType && copyStatus?.status === "loading"}
          buttonStyle="secondary"
        />
      )}
      <Button
        flexShrink="0"
        ml="xs"
        id="dl-button-custom"
        loading={downloadStatus?.status === "loading"}
        size="small"
        onClick={downloadCustomAsset}
        icon={getDownloadIcon()}
      />
    </ExportOption>
  );
};

export default CustomExportOption;
